import type { ReactNode } from 'react'

import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import type { TShareDataGetter } from '@/src/features/share/models/share.types'
import { UTM } from '@/src/general/constants/utm'
import { useUrlBuilder } from '@/src/general/hooks/useUrlBuilder'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { usePublicationShareStore } from '../../../stores/usePublicationShareStore'
import { PublicationShareModal } from './PublicationShareModal'

export const PublicationShareModalHost = (): ReactNode => {
  const { t } = useTranslate()
  const urlBuilder = useUrlBuilder()
  const { routes } = useRoutes()

  const publication = usePublicationShareStore((state) => state.publication)
  const trackingSource = usePublicationShareStore((state) => state.trackingSource)
  const close = usePublicationShareStore((state) => state.close)

  const publicationLinkUrl = !publication
    ? ''
    : urlBuilder.withUTM({
        route: routes.publication({ id: publication.id, slug: publication.slug, source: TRACKING_SOURCE.SHARE_LINK }),
        campaign: UTM.CAMPAIGN.COMMUNITY(publication.community.id),
      })

  const communityLinkUrl = !publication
    ? ''
    : urlBuilder.withUTM({
        route: routes.community({
          id: publication.community.id,
          slug: publication.community.slug,
          origin: EVENT_ORIGIN.PUBLICATION_SHARE,
        }),
        campaign: UTM.CAMPAIGN.COMMUNITY(publication.community.id),
      })

  const getPublicationShareData: TShareDataGetter = async () => {
    if (!publication) return { text: '', title: '', url: '' }
    const text = t('share.publication.email-body', {
      publicationName: publication.name,
      publicationUrl: publicationLinkUrl,
    })

    return { text, title: publication.name, url: publicationLinkUrl }
  }

  const getCommunityShareData: TShareDataGetter = async () => {
    if (!publication) return { text: '', title: '', url: '' }
    const text = t('share.share-by-email-body', {
      communityName: publication.community.name,
      lifetime: publication.community.lifetime,
      url: communityLinkUrl,
    })

    return { text, title: publication.community.name, url: communityLinkUrl }
  }

  return (
    <PublicationShareModal
      showModal={!!publication}
      onCancel={close}
      trackingSource={trackingSource || TRACKING_SOURCE.UNDEFINED}
      publication={publication}
      getPublicationShareData={getPublicationShareData}
      getCommunityShareData={getCommunityShareData}
    />
  )
}
